import React, { useCallback, useMemo } from 'react'
import { useStyles } from './Dimension.styles'
import { FormikProps, useFormikContext } from 'formik'
import { AgreementBasedOffer } from '../../../../modules/subscription/reducer'
import { CustomDimension } from './CustomDimension/CustomDimension'
import { StandardDimensionsV2 } from '../../../../common/components/StandardDimensionsV2/StandardDimensionsV2'
import {
  EntitlementOption,
  FieldMetadata,
  FieldMetadataField,
} from '../../../../types/flyout'

import {
  LabraToolTipV2,
  PlanType,
  RadioButtonGroupMapping,
  RadioButtons,
  RadioButtonsV2,
  RadioButtonType,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { Product } from '../../../../modules/productsListing/types'
import { useSelector } from '../../../../store'

interface DimensionsProps {
  product?: Product
  productId: string
  entitlementOption?: EntitlementOption
  isAgreementBasedOfferRenewal?: boolean
}

export const Dimension = ({
  product,
  productId,
  entitlementOption,
  isAgreementBasedOfferRenewal = false,
}: DimensionsProps) => {
  const formikProps = useFormikContext() as FormikProps<AgreementBasedOffer>
  const { values, setFieldValue, setFieldTouched } = formikProps
  const { classes } = useStyles()

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string
    setFieldValue('entitlementOption', value)
  }

  const fieldMetadata = useSelector<FieldMetadata | undefined>(state => {
    const root = state as {
      privateOffersInCreation?: {
        currentPrivateOfferInCreation?: Record<
          string,
          { fieldMetadata?: FieldMetadata }
        >
      }
    }
    return product && product.productId
      ? root.privateOffersInCreation?.currentPrivateOfferInCreation?.[
          product.productId
        ]?.fieldMetadata
      : undefined
  })
  const isEntitlementOptionReadOnly = (
    fieldMetadata?.entitlementOption as FieldMetadataField
  )?.readOnly
  const isEntitlementOptionHidden = (
    fieldMetadata?.entitlementOption as FieldMetadataField
  )?.hidden

  const dimensionSelectionMapping: RadioButtonGroupMapping[] = [
    {
      key: EntitlementOption.SINGLE,
      label: 'Buyer can choose only one unit of one of the existing dimensions',
      type: RadioButtonType.STANDARD,
    },
    {
      key: EntitlementOption.MANY,
      label: 'Buyer can choose one or more existing dimensions',
      type: RadioButtonType.STANDARD,
    },
  ]
  const FLEXIBLE_PAYMENT_RADIO_MAPPINGS = [
    {
      key: 'flex-false',
      label: 'Upfront payments',
      type: RadioButtonType.STANDARD,
    },
    {
      key: 'flex-true',
      label: 'Installment plan',
      type: RadioButtonType.STANDARD,
    },
  ]
  const flexiblePaymentCheckedValue = useMemo(
    () => ({
      key: values?.flexiblePaymentTerms
        ? 'flexiblePaymentTerms-true'
        : 'flexiblePaymentTerms-false',
      value: values?.flexiblePaymentTerms ? 'flex-true' : 'flex-false',
    }),
    [values?.flexiblePaymentTerms]
  )
  const handleFlexiblePaymentTermsChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const isFlexiblePaymentEnabled = e.target.value === 'flex-true'
      await setFieldValue('flexiblePaymentTerms', isFlexiblePaymentEnabled)
      if (isFlexiblePaymentEnabled && !values?.installments?.length) {
        await setFieldValue('installments', [
          { paymentDate: '', amount: null, currency: 'USD' },
        ])
      }
      await setFieldTouched('installments', false)
    },
    [setFieldTouched, setFieldValue, values?.installments]
  )
  return (
    <div className={classes.root}>
      {!(
        product?.productType === PlanType.SAAS_SUBSCRIPTION ||
        product?.productType === PlanType.AMI ||
        isAgreementBasedOfferRenewal
      ) && (
        <div>
          <div className={classes.pricingSection}>
            <div className={classes.pricingModelLabelRow}>
              <Typography className={classes.pricingModelTitle}>
                Pricing model
              </Typography>
              <LabraToolTipV2
                tooltipPlacement="top-start"
                description="Select whether the buyer will be billed upfront, through installments, or based on their usage."
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className={classes.pricingModelInfoIcon}
                />
              </LabraToolTipV2>
            </div>
            <div data-testid="flexiblePaymentTerms">
              <RadioButtonsV2
                radioButtonMappings={FLEXIBLE_PAYMENT_RADIO_MAPPINGS}
                checkedValue={flexiblePaymentCheckedValue}
                handleChange={handleFlexiblePaymentTermsChange}
              />
            </div>
          </div>
          <div className={classes.pricingDividerContainer}>
            <Divider />
          </div>
        </div>
      )}
      <>
        {' '}
        {entitlementOption &&
          !values.flexiblePaymentTerms &&
          product?.productType == PlanType.SAAS_CONTRACT &&
          !isEntitlementOptionHidden && (
            <>
              <Typography className={classes.sectionHeading}>
                Entitlement
              </Typography>
              <Typography className={classes.value}>
                Choose the entitlement type you want to offer
              </Typography>
              <RadioButtons
                checkedValue={{
                  key: entitlementOption,
                  value: entitlementOption,
                }}
                handleChange={handleSelection}
                radioButtonMappings={dimensionSelectionMapping.map(option => ({
                  ...option,
                  disabled: isEntitlementOptionReadOnly,
                }))}
              />
            </>
          )}
      </>

      <div className={classes.upperWrapper}>
        <StandardDimensionsV2
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
          addButtonName={'Add dimension'}
          productId={productId || ''}
          isFlexible={values.flexiblePaymentTerms}
          heading={'Product dimensions'}
          subHeading={'Customize the units for your listing'}
          product={product}
        />
      </div>
      {!(product?.productType == PlanType.SAAS_SUBSCRIPTION) && (
        <CustomDimension
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
          heading={'Custom dimensions'}
          productId={productId || ''}
          subHeading={
            'You can create new custom dimension only applicable for this offer.'
          }
          isFlexible={values.flexiblePaymentTerms}
        />
      )}
    </div>
  )
}
